import { useQuery } from "@tanstack/react-query";
import { LogOut, Settings, Shield, User } from "lucide-react";
import type React from "react";
import { useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { getMyProfile } from "@/api/profileApi";
import { useSupabaseSession } from "@/hooks/useSupabaseSession";
import { useTheme } from "@/hooks/useTheme";

const getThemeOptionClassName = (isSelected: boolean) =>
	[
		"rounded-md text-muted border px-2 py-1.5 text-xs font-medium transition-colors",
		isSelected ? "bg-primary-100 border-primary-500 shadow-sm" : "surface border-subtle soft-hover",
	].join(" ");

const UserProfileWidget: React.FC = () => {
	const { session } = useSupabaseSession();
	const navigate = useNavigate();
	const [isOpen, setIsOpen] = useState(false);
	const [theme, setTheme] = useTheme();

	const { data: myProfile } = useQuery({
		queryKey: ["profile", "me"],
		queryFn: ({ signal }) => getMyProfile({ signal }),
		enabled: !!session,
		retry: false,
	});

	const displayName = useMemo(() => {
		const user = session?.user;
		if (!user) return "Guest";
		return user.user_metadata?.full_name || user.user_metadata?.name || user.email || "User";
	}, [session?.user]);

	const email = session?.user?.email;
	const avatarUrl = session?.user?.user_metadata?.avatar_url as string | undefined;

	if (!session) return null;

	return (
		<div className="relative z-50">
			<div className="relative">
				<button
					type="button"
					onClick={() => setIsOpen((prev) => !prev)}
					className="flex items-center gap-2 rounded-lg border border-subtle surface px-2.5 py-1.5 soft-hover transition-colors"
					aria-haspopup="menu"
					aria-expanded={isOpen}
				>
					{avatarUrl ? (
						<img src={avatarUrl} alt={displayName} className="h-7 w-7 rounded-full border border-subtle object-cover" />
					) : (
						<div className="flex h-7 w-7 items-center justify-center rounded-full border border-subtle surface-muted">
							<User className="h-3.5 w-3.5 text-muted" />
						</div>
					)}
					<div className="min-w-0 text-left max-w-[140px]">
						<div className="truncate text-sm font-medium text-primary">{displayName}</div>
						{email && <div className="truncate text-xs text-muted">{email}</div>}
					</div>
				</button>
				{isOpen && (
					<div
						className="absolute right-0 top-full z-50 mt-2 w-56 overflow-hidden rounded-xl border border-subtle surface shadow-xl"
						role="menu"
					>
						<div className="border-b border-subtle surface-alt px-4 py-3">
							<div className="mb-2 text-[11px] font-semibold uppercase tracking-wide text-subtle">Theme</div>
							<fieldset className="grid grid-cols-2 gap-2" aria-label="Theme">
								<button
									type="button"
									onClick={() => setTheme("light")}
									aria-pressed={theme === "light"}
									className={getThemeOptionClassName(theme === "light")}
								>
									Light
								</button>
								<button
									type="button"
									onClick={() => setTheme("dark")}
									aria-pressed={theme === "dark"}
									className={getThemeOptionClassName(theme === "dark")}
								>
									Dark
								</button>
							</fieldset>
						</div>
						<button
							type="button"
							className="flex w-full items-center gap-2 px-4 py-3 text-left text-sm text-muted soft-hover"
							onClick={() => {
								setIsOpen(false);
								navigate("/user");
							}}
							role="menuitem"
						>
							<Settings className="h-4 w-4" />
							Profile
						</button>
						{myProfile?.profile.role === "ADMIN" && (
							<button
								type="button"
								className="flex w-full items-center gap-2 px-4 py-3 text-left text-sm text-muted soft-hover"
								onClick={() => {
									setIsOpen(false);
									navigate("/admin");
								}}
								role="menuitem"
							>
								<Shield className="h-4 w-4" />
								Admin Dashboard
							</button>
						)}
						<button
							type="button"
							className="flex w-full items-center gap-2 px-4 py-3 text-left text-sm text-muted soft-hover"
							onClick={() => {
								setIsOpen(false);
								navigate("/logout");
							}}
							role="menuitem"
						>
							<LogOut className="h-4 w-4" />
							Logout
						</button>
					</div>
				)}
			</div>
		</div>
	);
};

export default UserProfileWidget;
